import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  MIN_POP_DEFAULT,
  type CorrelationX,
  type CorrelationY,
  type ProjectionScenario,
} from "../lib/demographics";
import { CENSUS_PERIODS, type MapMetricId } from "../map/layers/demographics";

/**
 * What the People pane is currently asking.
 *
 * Split in three because the three questions are independent: what the map is
 * shaded by, what the forward scenario assumes, and what the scatter is
 * plotting. Changing one should never quietly reset the others.
 */
interface DemographicsState {
  /** Which measure the commune choropleth is shaded by. */
  metric: MapMetricId;
  /** Census period the map metric is read over, named by its closing year. */
  period: number;
  setMetric: (metric: MapMetricId) => void;
  setPeriod: (period: number) => void;

  scenario: ProjectionScenario;
  /** 0–1 — how much of an area's recent divergence carries forward. */
  persistence: number;
  /** Horizon year for the projection. */
  toYear: number;
  setScenario: (scenario: ProjectionScenario) => void;
  setPersistence: (persistence: number) => void;
  setToYear: (year: number) => void;

  correlationX: CorrelationX;
  correlationY: CorrelationY;
  /** Communes below this are dropped from the scatter as too small to read. */
  minPop: number;
  /** "all", or a department code to restrict the cross-section to. */
  depFilter: string;
  setCorrelationX: (x: CorrelationX) => void;
  setCorrelationY: (y: CorrelationY) => void;
  setMinPop: (pop: number) => void;
  setDepFilter: (dep: string) => void;
}

export const HORIZON_YEARS = [2030, 2040, 2050, 2070];

export const useDemographicsStore = create<DemographicsState>()(
  persist(
    (set) => ({
      metric: "growth",
      period: CENSUS_PERIODS[CENSUS_PERIODS.length - 1].to,
      setMetric: (metric) => set({ metric }),
      setPeriod: (period) => set({ period }),

      scenario: "central",
      persistence: 1,
      toYear: 2040,
      setScenario: (scenario) => set({ scenario }),
      setPersistence: (persistence) =>
        set({ persistence: Math.min(1, Math.max(0, persistence)) }),
      setToYear: (toYear) => set({ toYear }),

      correlationX: "growth_rate",
      correlationY: "price_growth",
      minPop: MIN_POP_DEFAULT,
      depFilter: "all",
      setCorrelationX: (correlationX) => set({ correlationX }),
      setCorrelationY: (correlationY) => set({ correlationY }),
      setMinPop: (minPop) => set({ minPop }),
      setDepFilter: (depFilter) => set({ depFilter }),
    }),
    {
      name: "mete-demographics",
      version: 1,
    },
  ),
);
